import React, { useState } from 'react';
import {
  HomefieldIconGgLogoBlack,
  HomefieldIconPlLogoBlack,
  HomefieldIconGggLogoBlack,
  HomefieldIconGgLogoHover,
  HomefieldIconPlLogoHover,
  HomefieldIconGggLogoHover,
} from '@dsg-tech/homefield-react-icons';
import { CTA, NavBarData, NikeConnected, StoreIdentifierName } from 'types';
import { setDataEmTag, checkStores } from 'utility/utility';
import ErrorBoundaryWithLogger from 'common/ErrorBoundary/ErrorBoundary';
import { getFeatureFlagByNameSelector } from 'store/selectors';
import useLogger from 'hooks/useLogger';
import { useAppSelector } from 'store/store';
import { multiUseAnalyticsEvent } from 'utility/analytics-events';
import TopMessage from './TopMessage';
import './TopNavBar.scss';

type Props = {
  navBarCtas: NavBarData[] | undefined;
  topMessageData: CTA[] | undefined;
  storeIdentifierName: StoreIdentifierName;
  showTopMessage: boolean;
  nikeConnected: NikeConnected;
};

export const createIcon = (name: string, isHovered: boolean) => {
  if (isHovered) {
    switch (name) {
      case 'publiclands':
        return HomefieldIconPlLogoHover;
      case 'golf_galaxy':
        return HomefieldIconGgLogoHover;
      case 'g3':
        return HomefieldIconGggLogoHover;
      default:
        return null;
    }
  } else {
    switch (name) {
      case 'publiclands':
        return HomefieldIconPlLogoBlack;
      case 'golf_galaxy':
        return HomefieldIconGgLogoBlack;
      case 'g3':
        return HomefieldIconGggLogoBlack;
      default:
        return null;
    }
  }
};

const brandsWithLogo = [
  {
    name: 'publiclands',
    link: 'https://www.publiclands.com',
  },
  {
    name: 'golf_galaxy',
    link: 'https://www.golfgalaxy.com',
  },
  {
    name: 'g3',
    link: 'https://www.goinggoinggone.com',
  },
];

export default function TopNavBar({
  navBarCtas,
  topMessageData,
  storeIdentifierName,
  showTopMessage,
  nikeConnected,
}: Props) {
  const { error } = useLogger();
  const redirectToOtherVerticalsEnabled = useAppSelector(
    getFeatureFlagByNameSelector('redirectToOtherVerticals'),
  );
  const favoriteIconEnabled = useAppSelector(getFeatureFlagByNameSelector('addFavoritesEnabled'));
  const nikeConnectedEnabled = useAppSelector(getFeatureFlagByNameSelector('nikeConnectedEnabled'));
  const [hoveredIcon, setHoveredIcon] = useState('');
  if (!navBarCtas) {
    // The errorBoundary in the parent catches this error
    throw new Error('navBarCtas is required');
  }
  const findLastThreeLinks = (i: number) => {
    const isFavoriteLinkAvailable = navBarCtas.some((element) => element.linkTitle === 'Favorites');
    const setLastItems = isFavoriteLinkAvailable ? 3 : 2;
    const lastIndex = navBarCtas?.length - setLastItems;

    if (i === lastIndex) {
      return ' border-left bold-text hmf-body-bold-s';
    }
    if (i > lastIndex) {
      return ' bold-text hmf-body-bold-s';
    }

    return '';
  };
  try {
    return (
      <div className="topNavBar-container">
        <div className="topNavBar-container__content-wrapper">
          {redirectToOtherVerticalsEnabled && (
            <div className="enterprise-brand-links hmf-pl-s hmf-display-flex hmf-align-items-center hmf-flex-wrap hmf-justify-content-space-around hmf-align-content-center">
              {brandsWithLogo.map(({ name, link }) => {
                const IconComponent = createIcon(name, hoveredIcon === name);
                return (
                  <a
                    key={name}
                    href={`${link}?camp=DRT:HeaderLink:${storeIdentifierName.toUpperCase()}`}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={name}>
                    <IconComponent
                      className={`header2-pl-other-brands-logo ${name}`}
                      onMouseOver={() => setHoveredIcon(name)}
                      onFocus={() => setHoveredIcon(name)}
                      onMouseOut={() => setHoveredIcon('')}
                      onBlur={() => setHoveredIcon('')}
                    />
                  </a>
                );
              })}
            </div>
          )}
          {showTopMessage && (
            <ErrorBoundaryWithLogger componentId="TopNavBar-TopMessage">
              <TopMessage
                topMessageData={topMessageData}
                storeIdentifierName={storeIdentifierName}
              />
            </ErrorBoundaryWithLogger>
          )}
          <div className="topNavBar-container__text-container hmf-py-xs">
            {nikeConnected && nikeConnected.value === 'true' && nikeConnectedEnabled && (
              <a
                key="link-dicks-nike"
                href="/f/all-nike-connected-membership"
                target="_self"
                title="DICK'S | Nike"
                data-em={setDataEmTag('Header', 'header_dicks_nike')}
                className="topNavBar-container__text-container--links hmf-body-s hmf-m-0 hmf-px-s">
                DICK&#39;S | Nike
                <div className="hidden-item">DICK&#39;S | Nike</div>
              </a>
            )}

            {navBarCtas
              .filter((links) => (favoriteIconEnabled ? links : links.linkTitle !== 'Favorites'))
              .map((links: CTA, index: number) => (
                <a
                  key={`link-${links.linkTitle}`}
                  href={checkStores(links.link)}
                  onClick={() =>
                    links.linkTitle === 'Favorites'
                      ? multiUseAnalyticsEvent({
                          event: 'Signed out athlete clicked Favorites from:',
                          value: 'Header',
                        })
                      : {}
                  }
                  target={checkStores(links.link)?.includes('http') ? '_blank' : '_self'}
                  title={links.linkTitle}
                  data-em={setDataEmTag('Header', links.linkTitle)}
                  className={`topNavBar-container__text-container--links hmf-body-s hmf-m-0 hmf-px-s${findLastThreeLinks(
                    index,
                  )}`}
                  rel={checkStores(links.link)?.includes('http') ? 'noreferrer' : ''}>
                  {links.linkTitle}
                  <div className="hidden-item hmf-body-bold-s">{links.linkTitle}</div>
                </a>
              ))}
          </div>
        </div>
      </div>
    );
  } catch (e) {
    error('Could not load TopNavBar - ', e);
    return null;
  }
}
